import { AxiosResponse } from 'axios';
import { apiClient } from '../instance';
import {
  PaginatedTickets,
  TicketDetail,
  TicketCreatePayload,
  TicketOperatorCreatePayload,
  TicketUpdatePayload,
  TicketMessage,
  MessageCreatePayload,
  TicketFilters,
  TicketAttachment,
} from '../../types/ticketInterfaces';

const BASE = '/helpdesk/tickets';

/**
 * Собирает query-параметры из фильтров (убирая пустые).
 */
function buildParams(
  filters: Partial<TicketFilters>,
  page: number,
  limit: number,
): Record<string, string | number> {
  const params: Record<string, string | number> = {
    limit,
    offset: (page - 1) * limit,
  };
  if (filters.status) params.status = filters.status;
  if (filters.priority) params.priority = filters.priority;
  if (filters.assigned_to) params.assigned_to = filters.assigned_to;
  if (filters.channel) params.channel = filters.channel;
  if (filters.ticket_type) params.ticket_type = filters.ticket_type;
  if (filters.search) params.search = filters.search;
  if (filters.date_from) params.created_at__gte = filters.date_from;
  if (filters.date_to) params.created_at__lte = filters.date_to;
  if (filters.ordering) params.ordering = filters.ordering;
  return params;
}

export const ticketsApi = {
  /* ── Список ── */
  getTickets: (
    filters: Partial<TicketFilters> = {},
    page = 1,
    limit = 100,
  ): Promise<AxiosResponse<PaginatedTickets>> =>
    apiClient.get<PaginatedTickets>(`${BASE}/`, {
      params: buildParams(filters, page, limit),
    }),

  /* ── Детали тикета ── */
  getTicketById: (id: number): Promise<AxiosResponse<TicketDetail>> =>
    apiClient.get<TicketDetail>(`${BASE}/${id}/`),

  /* ── Создание (клиент) ── */
  createTicket: (
    data: TicketCreatePayload,
    files?: File[],
  ): Promise<AxiosResponse<TicketDetail>> => {
    if (files && files.length > 0) {
      const formData = new FormData();
      formData.append('subject', data.subject);
      formData.append('description', data.description);
      if (data.priority) formData.append('priority', data.priority);
      if (data.ticket_type) formData.append('ticket_type', String(data.ticket_type));
      files.forEach((f) => formData.append('files', f));
      return apiClient.post<TicketDetail>(`${BASE}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return apiClient.post<TicketDetail>(`${BASE}/`, data);
  },

  /* ── Создание (оператор) ── */
  createTicketOperator: (
    data: TicketOperatorCreatePayload,
  ): Promise<AxiosResponse<TicketDetail>> =>
    apiClient.post<TicketDetail>(`${BASE}/`, data),

  /* ── Обновление (PATCH) ── */
  updateTicket: (
    id: number,
    data: TicketUpdatePayload,
  ): Promise<AxiosResponse<TicketDetail>> =>
    apiClient.patch<TicketDetail>(`${BASE}/${id}/`, data),

  /* ── Решить заявку (POST solve/) ── */
  solveTicket: (id: number): Promise<AxiosResponse<TicketDetail>> =>
    apiClient.post<TicketDetail>(`${BASE}/${id}/solve/`),

  /* ── Закрыть заявку (POST close/) ── */
  closeTicket: (id: number): Promise<AxiosResponse<TicketDetail>> =>
    apiClient.post<TicketDetail>(`${BASE}/${id}/close/`),

  /* ── Назначить оператора (POST assign/) ── */
  assignTicket: (
    id: number,
    operatorId: number,
  ): Promise<AxiosResponse<TicketDetail>> =>
    apiClient.post<TicketDetail>(`${BASE}/${id}/assign/`, {
      operator_id: operatorId,
    }),

  /* ── Получить сообщения (GET messages/) ── */
  getMessages: (ticketId: number): Promise<AxiosResponse<TicketMessage[]>> =>
    apiClient.get<TicketMessage[]>(`${BASE}/${ticketId}/messages/`),

  /* ── Сообщения с автопереводом (GET messages/translated/) ── */
  getMessagesTranslated: (
    ticketId: number,
    lang?: string,
  ): Promise<AxiosResponse<TicketMessage[]>> =>
    apiClient.get<TicketMessage[]>(`${BASE}/${ticketId}/messages/translated/`, {
      params: lang ? { lang } : undefined,
    }),

  /* ── Отправить сообщение (POST messages/) ── */
  addMessage: (
    ticketId: number,
    data: MessageCreatePayload,
  ): Promise<AxiosResponse<TicketMessage>> =>
    apiClient.post<TicketMessage>(`${BASE}/${ticketId}/messages/`, data),

  /* ── Отправить сообщение с файлами (multipart через messages/) ── */
  addMessageWithFiles: (
    ticketId: number,
    body: string,
    files: File[],
    isInternal: boolean = false,
  ): Promise<AxiosResponse<TicketMessage>> => {
    const formData = new FormData();
    formData.append('body', body);
    formData.append('is_internal', String(isInternal));
    files.forEach((f) => formData.append('files', f));
    return apiClient.post<TicketMessage>(
      `${BASE}/${ticketId}/messages/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },
};
